import type { CSSProperties, KeyboardEvent, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";
import type {
  WorkbenchScatterAxis,
  WorkbenchScatterPoint,
  WorkbenchScatterThresholds,
  WorkbenchVisualizationTone,
} from "./WorkbenchScatterPlot";
import {
  workbenchVisualizationToneColor as toneColor,
  workbenchVisualizationToneColorWithAlpha as toneColorWithAlpha,
} from "./workbenchVisualizationTone";

export interface WorkbenchRadarQuadrant {
  id: "low-high" | "high-high" | "low-low" | "high-low";
  title: ReactNode;
  description?: ReactNode;
}

export interface WorkbenchRadarLegendItem {
  id: string;
  label: ReactNode;
  tone?: WorkbenchVisualizationTone;
}

export interface WorkbenchRadarMatrixProps {
  points: WorkbenchScatterPoint[];
  xAxis: WorkbenchScatterAxis;
  yAxis: WorkbenchScatterAxis;
  thresholds?: WorkbenchScatterThresholds;
  quadrants?: WorkbenchRadarQuadrant[];
  legend?: WorkbenchRadarLegendItem[];
  selectedPointId?: string;
  onPointSelect?: (point: WorkbenchScatterPoint) => void;
  width?: number;
  height?: number;
  labelLimit?: number;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
}

const finiteValues = (values: number[]) => values.filter(Number.isFinite);

const getDomain = (values: number[], min?: number, max?: number) => {
  const candidates = finiteValues(values);
  const lower = Number.isFinite(min) ? Number(min) : Math.min(...candidates, 0);
  const upper = Number.isFinite(max) ? Number(max) : Math.max(...candidates, 1);
  if (lower === upper) return [lower, upper + 1] as const;
  return lower < upper ? ([lower, upper] as const) : ([upper, lower] as const);
};

const scale = (
  value: number,
  domain: readonly [number, number],
  start: number,
  end: number,
) => start + ((value - domain[0]) / (domain[1] - domain[0])) * (end - start);

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const activatePoint = (
  event: KeyboardEvent<SVGGElement>,
  point: WorkbenchScatterPoint,
  onPointSelect?: (point: WorkbenchScatterPoint) => void,
) => {
  if (!onPointSelect || (event.key !== "Enter" && event.key !== " ")) return;
  event.preventDefault();
  onPointSelect(point);
};

const quadrantPosition = {
  "low-high": { x: 26, y: 8, anchor: "start" as const },
  "high-high": { x: 974, y: 8, anchor: "end" as const },
  "low-low": { x: 26, y: 660, anchor: "start" as const },
  "high-low": { x: 974, y: 660, anchor: "end" as const },
};

export function WorkbenchRadarMatrix({
  points,
  xAxis,
  yAxis,
  thresholds,
  quadrants = [],
  legend = [],
  selectedPointId,
  onPointSelect,
  width = 1000,
  height = 700,
  labelLimit = 18,
  emptyState = null,
  ariaLabel = `${xAxis.label} by ${yAxis.label} radar matrix`,
  className,
}: WorkbenchRadarMatrixProps) {
  const validPoints = points.filter(
    (point) => Number.isFinite(point.x) && Number.isFinite(point.y),
  );
  if (!validPoints.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  const xDomain = getDomain(
    validPoints.map((point) => point.x),
    xAxis.min,
    xAxis.max,
  );
  const yDomain = getDomain(
    validPoints.map((point) => point.y),
    yAxis.min,
    yAxis.max,
  );
  const sizeDomain = getDomain(validPoints.map((point) => point.size ?? 1));
  const plotLeft = 0;
  const plotRight = width;
  const plotTop = 0;
  const plotBottom = height;
  const xThreshold = clamp(
    scale(
      Number.isFinite(thresholds?.x) ? Number(thresholds?.x) : (xDomain[0] + xDomain[1]) / 2,
      xDomain,
      plotLeft,
      plotRight,
    ),
    plotLeft,
    plotRight,
  );
  const yThreshold = clamp(
    scale(
      Number.isFinite(thresholds?.y) ? Number(thresholds?.y) : (yDomain[0] + yDomain[1]) / 2,
      yDomain,
      plotBottom,
      plotTop,
    ),
    plotTop,
    plotBottom,
  );
  const labelledPointIds = new Set(
    [...validPoints]
      .sort((left, right) => (right.size ?? 0) - (left.size ?? 0))
      .slice(0, labelLimit)
      .map((point) => point.id),
  );

  return (
    <div
      data-monkeys-component="workbench-radar-matrix"
      className={cn("relative size-full min-h-[42rem] min-w-0 overflow-hidden", className)}
      style={{
        backgroundColor: "hsl(var(--radar-canvas, var(--background)))",
        backgroundImage:
          "radial-gradient(circle at 78% 20%, hsl(var(--primary) / 0.08), transparent 32%), radial-gradient(circle at 18% 82%, hsl(var(--radar-series-two, var(--accent-foreground)) / 0.06), transparent 30%)",
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="absolute inset-0 size-full"
        role="img"
        aria-label={ariaLabel}
      >
        <g aria-hidden="true">
          {Array.from({ length: 13 }, (_, index) => {
            const x = (width / 12) * index;
            const y = (height / 12) * index;
            return (
              <g key={index}>
                <line
                  x1={x}
                  y1={0}
                  x2={x}
                  y2={height}
                  style={{ stroke: "hsl(var(--radar-grid, var(--border)) / 0.36)" }}
                />
                <line
                  x1={0}
                  y1={y}
                  x2={width}
                  y2={y}
                  style={{ stroke: "hsl(var(--radar-grid, var(--border)) / 0.36)" }}
                />
              </g>
            );
          })}
          <line
            x1={xThreshold}
            y1={0}
            x2={xThreshold}
            y2={height}
            strokeWidth="1.5"
            style={{ stroke: "hsl(var(--radar-axis, var(--primary)) / 0.64)" }}
          />
          <line
            x1={0}
            y1={yThreshold}
            x2={width}
            y2={yThreshold}
            strokeWidth="1.5"
            style={{ stroke: "hsl(var(--radar-axis, var(--primary)) / 0.64)" }}
          />
        </g>

        {quadrants.map((quadrant) => {
          const position = quadrantPosition[quadrant.id];
          return (
            <g key={quadrant.id} aria-hidden="true">
              <text
                x={position.x}
                y={position.y + 20}
                textAnchor={position.anchor}
                className="fill-muted-foreground text-[0.8125rem] font-semibold"
              >
                {quadrant.title}
              </text>
              {hasRenderableNode(quadrant.description) && typeof quadrant.description === "string" ? (
                <text
                  x={position.x}
                  y={position.y + 40}
                  textAnchor={position.anchor}
                  className="fill-muted-foreground text-[0.6875rem]"
                >
                  {quadrant.description}
                </text>
              ) : null}
            </g>
          );
        })}

        {validPoints.map((point, index) => {
          const x = clamp(scale(point.x, xDomain, 40, width - 40), 40, width - 40);
          const y = clamp(scale(point.y, yDomain, height - 48, 54), 54, height - 48);
          const tone = point.tone || (index % 2 === 0 ? "primary" : "accent");
          const selected = selectedPointId === point.id;
          const labelled = labelledPointIds.has(point.id);
          const radius = labelled
            ? clamp(scale(point.size ?? 1, sizeDomain, 5, 9), 5, 9)
            : 3.5;
          const fontSize = labelled
            ? clamp(scale(point.size ?? 1, sizeDomain, 14, 31), 14, 31)
            : 0;
          const pointStyle: CSSProperties = {
            fill: toneColor(tone),
            stroke: "hsl(var(--foreground) / 0.74)",
          };
          const labelStyle: CSSProperties = {
            fill: toneColor(tone),
            filter: `drop-shadow(0 0 0.65rem ${toneColorWithAlpha(tone, 0.72)})`,
          };
          return (
            <g
              key={point.id}
              transform={`translate(${x}, ${y})`}
              role={onPointSelect ? "button" : undefined}
              tabIndex={onPointSelect ? 0 : undefined}
              aria-label={`${point.label}: ${xAxis.label} ${point.x}, ${yAxis.label} ${point.y}`}
              aria-pressed={onPointSelect ? selected : undefined}
              onClick={onPointSelect ? () => onPointSelect(point) : undefined}
              onKeyDown={(event) => activatePoint(event, point, onPointSelect)}
              className={cn(onPointSelect && "cursor-pointer outline-none")}
            >
              {selected ? (
                <circle
                  r={radius + 10}
                  fill="none"
                  strokeWidth="2"
                  style={{ stroke: toneColorWithAlpha(tone, 0.48) }}
                />
              ) : null}
              <circle r={radius} strokeWidth="1.25" style={pointStyle} />
              {labelled ? (
                <text
                  y={-radius - 8}
                  textAnchor="middle"
                  fontSize={fontSize}
                  fontWeight="700"
                  style={labelStyle}
                  className="pointer-events-none"
                >
                  {point.label.slice(0, 22)}
                </text>
              ) : null}
              <title>{point.description || point.label}</title>
            </g>
          );
        })}

        <circle
          cx={xThreshold}
          cy={yThreshold}
          r="10"
          style={{ fill: "hsl(var(--radar-axis, var(--primary)))" }}
        />
        <circle cx={xThreshold} cy={yThreshold} r="3" className="fill-primary-foreground" />
        <text
          x={width / 2}
          y={height - 12}
          textAnchor="middle"
          className="fill-muted-foreground text-[0.6875rem]"
        >
          {xAxis.label}
        </text>
        <text
          x="14"
          y={height / 2}
          textAnchor="middle"
          transform={`rotate(-90 14 ${height / 2})`}
          className="fill-muted-foreground text-[0.6875rem]"
        >
          {yAxis.label}
        </text>
      </svg>

      <div
        className="absolute bottom-4 left-4 hidden w-44 overflow-hidden rounded-[var(--radius)] border bg-card/95 shadow-xl sm:block"
        style={{ borderColor: "hsl(var(--radar-border, var(--border)))" }}
        aria-hidden="true"
      >
        <div className="flex h-9 items-center justify-between border-b border-border px-3 text-xs font-semibold text-muted-foreground">
          <span>−</span><span>100%</span><span>＋</span>
        </div>
        <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full">
          <rect width={width} height={height} style={{ fill: "hsl(var(--radar-canvas, var(--background)))" }} />
          <line x1={xThreshold} y1={0} x2={xThreshold} y2={height} style={{ stroke: "hsl(var(--radar-axis, var(--primary)) / 0.7)" }} strokeWidth="6" />
          <line x1={0} y1={yThreshold} x2={width} y2={yThreshold} style={{ stroke: "hsl(var(--radar-axis, var(--primary)) / 0.7)" }} strokeWidth="6" />
          {validPoints.map((point, index) => (
            <circle
              key={point.id}
              cx={clamp(scale(point.x, xDomain, 40, width - 40), 40, width - 40)}
              cy={clamp(scale(point.y, yDomain, height - 48, 54), 54, height - 48)}
              r="8"
              style={{ fill: toneColor(point.tone || (index % 2 === 0 ? "primary" : "accent")) }}
            />
          ))}
          <rect
            x={width * 0.22}
            y={height * 0.12}
            width={width * 0.58}
            height={height * 0.62}
            fill="none"
            strokeWidth="8"
            style={{ stroke: "hsl(var(--primary))" }}
          />
        </svg>
      </div>

      {legend.length ? (
        <div className="absolute bottom-4 left-1/2 hidden -translate-x-1/2 items-center gap-4 rounded-full border border-border bg-card/95 px-4 py-2 text-xs font-semibold shadow-xl md:flex">
          {legend.map((item) => {
            const tone = item.tone || "muted";
            return (
              <span key={item.id} className="flex items-center gap-2 text-muted-foreground">
                <span className="size-2 rounded-full" style={{ backgroundColor: toneColor(tone) }} />
                {item.label}
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
